/*
 * Copyright © 2017-2026 Knowledge for Decisions (K4D). All rights reserved.
 *
 * This code and associated materials are protected by copyright laws and international
 * treaties. Unauthorized use, reproduction, distribution, or modification of any content
 * from Knowledge for Decisions (K4D) is strictly prohibited. For inquiries regarding
 * the use or licensing of K4D's opyrighted materials, please contact us at
 * contact@k4d.com.br. "Knowledge for Decisions (K4D)" and the slogan "Using data to make
 * precise decisions" are registered trademarks of Knowledge for Decisions (K4D).
 */

/**
 * Choosing which seats of one plan empty when the reduction lands.
 *
 * The row is the control, not a box beside a label. The shared Checkbox reads
 * its value from the form store by name and there is no store here — the
 * choice is a list this component is handed — and the whole row being
 * clickable is what the screen describes anyway.
 */

import React from 'react';
import { useTranslation } from 'react-i18next';
import { Box, Text, UnstyledButton } from '@/shared/ui';
import {
  canChoose, choose, isLocked, stillToChoose, unchoose,
  type Candidate, type LeavingEntry,
} from './seat-reduction';

const TickIcon: React.FC = () => (
  <svg
    width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor"
    strokeWidth={3} strokeLinecap="round" strokeLinejoin="round" aria-hidden="true"
  >
    <path d="M20 6 9 17l-5-5" />
  </svg>
);

/**
 * Is same.
 */
const isSame = (a: LeavingEntry, b: LeavingEntry): boolean => (
  a.kind === b.kind
  && (a.seat_id ?? null) === (b.seat_id ?? null)
  && (a.invitation_id ?? null) === (b.invitation_id ?? null)
  && (a.slot ?? null) === (b.slot ?? null)
);

/**
 * One plan's candidates, and how many of them still need choosing.
 *
 * A full block draws its remaining candidates disabled rather than swallowing
 * the click: whoever clicks means "this one too", and a screen that answered
 * by releasing somebody else would be deciding who leaves on the operator's
 * behalf.
 */
export const LeavingPicker: React.FC<{
  planId: string;
  planTitle: string;
  capacity: number;
  candidates: Candidate[];
  leaving: LeavingEntry[];
  onChange: (leaving: LeavingEntry[]) => void;
}> = ({ planId, planTitle, capacity, candidates, leaving, onChange }) => {
  const { t } = useTranslation('features/subscription');
  const missing = stillToChoose(leaving, planId, { [planId]: capacity });
  const chosen = capacity - missing;

  const labelFor = (candidate: Candidate): string => {
    if (candidate.kind === 'vacant') return t('v2.seats.nobodyUsesThisSeat');
    if (candidate.kind === 'invitation') {
      return t('v2.seats.pendingInvite', { email: candidate.email });
    }
    return candidate.email ?? '';
  };

  return (
    <div
      data-testid={`plan-seat-pick-${planId}`}
      style={{ display: 'flex', flexDirection: 'column', gap: 'var(--space-2)' }}
    >
      <div
        style={{
          display: 'flex', alignItems: 'baseline', justifyContent: 'space-between',
          gap: 'var(--space-3)',
        }}
      >
        <Text
          component="span"
          style={{
            fontSize: 'var(--text-small)', fontWeight: 'var(--font-bold)',
            color: 'var(--text-primary)',
          }}
        >
          {capacity === 1
            ? t('v2.seats.whichSeatLeaves', { plan: planTitle })
            : t('v2.seats.whichSeatsLeave', { count: capacity, plan: planTitle })}
        </Text>
        <Text
          component="span"
          style={{
            fontSize: 'var(--text-meta)', fontWeight: 'var(--font-bold)',
            color: missing === 0
              ? 'var(--status-success-text)'
              : 'var(--text-tertiary-soft)',
          }}
        >
          {t('v2.seats.chosenOf', { chosen, total: capacity })}
        </Text>
      </div>

      {candidates.map((candidate, index) => {
        const marked = leaving.find((entry) => isSame(entry, candidate));
        const locked = marked ? isLocked(marked) : false;
        const allowed = canChoose(leaving, candidate, capacity);
        return (
          <div
            key={`${candidate.kind}-${candidate.seat_id ?? candidate.invitation_id ?? candidate.slot ?? index}`}
            style={{
              display: 'flex', alignItems: 'center', gap: 'var(--space-2-5)',
              opacity: allowed || marked ? 1 : 0.45,
            }}
          >
            <UnstyledButton
              role="checkbox"
              aria-checked={Boolean(marked)}
              disabled={!allowed}
              onClick={() => onChange(
                marked ? unchoose(leaving, candidate) : choose(leaving, candidate, capacity),
              )}
              style={{
                display: 'flex', alignItems: 'center', gap: 'var(--space-2-5)',
                background: 'none', textAlign: 'left',
                cursor: allowed ? 'pointer' : 'not-allowed',
              }}
            >
              <Box
                style={{
                  width: 18, height: 18, flex: 'none',
                  borderRadius: 'var(--radius-sm)',
                  border: `1px solid ${marked ? 'var(--seed-base)' : 'var(--border-medium)'}`,
                  background: marked ? 'var(--seed-base)' : 'var(--surface-primary)',
                  display: 'flex', alignItems: 'center', justifyContent: 'center',
                  color: 'var(--text-inverse)',
                }}
              >
                {marked && <TickIcon />}
              </Box>
              <Text
                component="span"
                style={{
                  fontSize: 'var(--text-small)', color: 'var(--text-primary)',
                  textDecoration: locked ? 'line-through' : 'none',
                }}
              >
                {labelFor(candidate)}
              </Text>
            </UnstyledButton>
            {locked && (
              <Text
                component="span"
                style={{
                  fontSize: 'var(--text-tag)', color: 'var(--text-tertiary-soft)',
                }}
              >
                {t('v2.seats.inviteAlreadyCancelled')}
              </Text>
            )}
            {!locked && candidate.kind !== 'person' && (
              <Text
                component="span"
                style={{
                  fontSize: 'var(--text-tag)', color: 'var(--text-tertiary-soft)',
                }}
              >
                {t('v2.seats.freesNobody')}
              </Text>
            )}
          </div>
        );
      })}

      <Text
        style={{
          fontSize: 'var(--text-tag)', lineHeight: 1.5,
          color: 'var(--text-tertiary-soft)',
        }}
      >
        {t('v2.seats.pickerNote')}
      </Text>
    </div>
  );
};
